import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { submitLead } from "../services/apiService";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+1",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await submitLead(formData);

      if (result.success) {
        toast.success(result.message);
        // Redirect to application page
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Trademark Flare - Canadian IP Experts"
        description="Connect with Trademark Flare for expert Canadian trademark registration guidance. Our CIPO specialists in Halifax are ready to protect your brand."
        keywords="contact trademark flare, trademark registration Halifax, Canadian IP consultation, CIPO services contact"
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-white mb-6">
              Let's Connect
            </h1>
            <p className="text-xl text-brand-100 leading-relaxed">
              Questions about protecting your brand? Trademark Flare's expert
              team is here to guide you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number *
                  </label>
                  <div className="flex gap-3">
                    <select
                      value={formData.countryCode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          countryCode: e.target.value,
                        })
                      }
                      className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    >
                      <option value="+1">🇨🇦 +1 (Canada)</option>
                      <option value="+1">🇺🇸 +1 (USA)</option>
                      <option value="+44">🇬🇧 +44 (UK)</option>
                      <option value="+91">🇮🇳 +91 (India)</option>
                      <option value="+86">🇨🇳 +86 (China)</option>
                      <option value="+81">🇯🇵 +81 (Japan)</option>
                      <option value="+82">🇰🇷 +82 (South Korea)</option>
                      <option value="+61">🇦🇺 +61 (Australia)</option>
                      <option value="+49">🇩🇪 +49 (Germany)</option>
                      <option value="+33">🇫🇷 +33 (France)</option>
                      <option value="+39">🇮🇹 +39 (Italy)</option>
                      <option value="+34">🇪🇸 +34 (Spain)</option>
                      <option value="+7">🇷🇺 +7 (Russia)</option>
                      <option value="+55">🇧🇷 +55 (Brazil)</option>
                      <option value="+52">🇲🇽 +52 (Mexico)</option>
                      <option value="+27">🇿🇦 +27 (South Africa)</option>
                      <option value="+971">🇦🇪 +971 (UAE)</option>
                      <option value="+65">🇸🇬 +65 (Singapore)</option>
                      <option value="+31">🇳🇱 +31 (Netherlands)</option>
                      <option value="+46">🇸🇪 +46 (Sweden)</option>
                    </select>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) {
                          setFormData({ ...formData, phone: value });
                        }
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      placeholder="4165550123"
                      maxLength="10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your trademark needs..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold py-4 px-6 rounded-lg hover:from-brand-700 hover:to-brand-900 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Contact Information
                </h2>

                <div className="space-y-4">
                  <div className="flex items-start space-x-4 p-4 bg-brand-50 rounded-xl">
                    <div className="bg-brand-600 p-3 rounded-lg flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Office Location
                      </h3>
                      <p className="text-gray-600">
                        6156 Quinpool Rd, Halifax, NS B3L 1A3, Canada
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-brand-50 rounded-xl">
                    <div className="bg-brand-600 p-3 rounded-lg flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Phone
                      </h3>
                      <p className="text-gray-600">
                        <a
                          href="tel:+13126802924"
                          className="hover:text-brand-600 transition-colors"
                        >
                          +1 (312) 680-2924
                        </a>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Mon-Fri, 9:00 AM - 6:00 PM AST
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-brand-50 rounded-xl">
                    <div className="bg-brand-600 p-3 rounded-lg flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Email
                      </h3>
                      <p className="text-gray-600">
                        <a
                          href="mailto:info@trademarkflare.com"
                          className="hover:text-brand-600 transition-colors"
                        >
                          info@trademarkflare.com
                        </a>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        We respond within 24 hours
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-brand-50 rounded-xl">
                    <div className="bg-brand-600 p-3 rounded-lg flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Business Hours
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Monday - Friday: 9:00 AM - 6:00 PM AST
                      </p>
                      <p className="text-gray-600 text-sm">
                        Saturday: 10:00 AM - 2:00 PM AST
                      </p>
                      <p className="text-gray-600 text-sm">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white shadow-lg">
                <h3 className="text-2xl font-bold mb-4">
                  Need Immediate Assistance?
                </h3>
                <p className="text-brand-100 mb-6">
                  Our trademark experts at Trademark Flare are standing by to
                  help you navigate the CIPO registration process with
                  confidence.
                </p>
                <a
                  href="tel:+18669788034"
                  className="inline-block bg-white text-brand-600 font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-all duration-200"
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
